import './Cart.scss'

import * as H from 'history'

import {FormProduct, FormProducts, OrderForm} from './OrderForm'
import {FormProvider, useForm} from 'react-hook-form'
import React, {useEffect, useState} from 'react'
import {useHistory, useLocation} from 'react-router'

import {CartAbandoned} from '../../layout/cart/CartAbandoned'
import {CartProductOverview} from '../../layout/cart/CartProductOverview'
import {Flipper} from 'react-flip-toolkit'
import {Hero} from '../../../layout/Hero'
import {Layout} from '../../../layout/Layout'
import {RouteLeavingGuard} from 'views/ui/RouteLeavingGuard'
import {style} from '../../../../util/style'
import {useCart} from '../../../../util/cart'
import {useSameHistoryListener} from '../../../../util/history'
import {wrap} from '@codeurs/front'

export type DeliveryType = {
	path: string
	title: string
	price?: string
	type: 'delivery' | 'takeaway'
}

const styles = style('cart')

export const Cart: React.FC = wrap(Layout)(
	({
		recaptcha_client_key,
		shippingcosts,
		schools,
		regional_services,
		...props
	}) => {
		const history = useHistory()
		const location = useLocation()
		const [cartConfirmed, setCartConfirmed] = useState(false)
		const [formCompleted, setFormCompleted] = useState(false)
		const methods = useForm({
			shouldUnregister: false,
			defaultValues: {
				shippingcosts: shippingcosts[0]?.path
			}
		})
		const {getValues, watch} = methods
		useSameHistoryListener(() => setCartConfirmed(false))
		const watchShippingCosts = watch('shippingcosts')

		const selectedDelivery: DeliveryType =
			shippingcosts.find(
				(shipping: DeliveryType) => shipping.path === watchShippingCosts
			) || shippingcosts[0]

		const {hero, title, basketabandon, products, variations} = props
		const {cart} = useCart()
		const cartProducts = cart.productsPerId
		const hasItems = cartProducts && cartProducts.length > 0

		const changableKey = JSON.stringify(cart.products).replace(/[\W_]+/g, '')
		const productsForForm: FormProducts = hasItems && generateProductsForForm()
		return (
			<div className={styles}>
				<FormProvider {...methods}>
					<Flipper flipKey={changableKey} spring="stiff">
						<Hero {...hero} title={(hero && hero.title) || title} />
						{!cartConfirmed && (
							<CartProductOverview
								products={products}
								variations={variations}
								shippingcosts={shippingcosts}
								onConfirm={() => setCartConfirmed(true)}
							/>
						)}
						{cartConfirmed && (
							<OrderForm
								selectedDelivery={selectedDelivery}
								schools={schools}
								products={productsForForm}
								regionalServices={regional_services}
								formCompleted={() => setFormCompleted(true)}
								reCaptchaKey={recaptcha_client_key}
							/>
						)}
						<RouteLeavingGuard
							when={!formCompleted}
							shouldBlockNavigation={(nextLocation: H.Location) => {
								if (location.pathname === nextLocation.pathname) return false
								if (hasItems && !formCompleted) return true
								return false
							}}
							disableExternalNavigationBlocking={!hasItems}
							navigate={(path) => history.push(path)}
						>
							{({setModalVisible, modalVisible, setConfirmedNavigation}) => (
								<CartAbandoned
									isOpen={modalVisible}
									onCancel={() => setModalVisible(false)}
									onConfirm={() => {
										setModalVisible(false)
										setConfirmedNavigation(true)
									}}
									{...basketabandon}
								/>
							)}
						</RouteLeavingGuard>
					</Flipper>
				</FormProvider>
			</div>
		)
	}
)

const generateProductsForForm = () => {
	const {cart} = useCart()

	return cart.productsPerId.map((product) => {
		const formProduct: FormProduct = {
			id: parseInt(product.productId),
			variants: []
		}
		const hasVariants = !!Object.values(product?.variants[0]?.meta)[0]
		if (hasVariants) {
			product.variants.forEach((variant) => {
				const metaValue =
					variant?.meta && parseInt(Object.values(variant.meta)[0]?.toString())
				formProduct.variants.push({
					id: metaValue,
					quantity: cart.quantityOf(variant)
				})
			})
		} else {
			formProduct.quantity = cart.quantityOfVariantsWith(product.productId)
		}
		return formProduct
	})
}
